import { supabase } from '../lib/supabase'
import type { Intervalo } from '../utils/periodo'
import type { LeadBasico } from '../utils/leads'

export type Lead = LeadBasico & {
  id: string
  marcaId: string
  fonteId: string
  recebidoEm: string
  campos: Record<string, unknown>
  ip: string | null
  userAgent: string | null
}

type Linha = Record<string, unknown>
const txt = (v: unknown) => (typeof v === 'string' && v ? v : null)

function paraLead(r: Linha): Lead {
  return {
    id: r.id as string,
    marcaId: r.marca_id as string,
    marca: (r.marca as string) ?? '',
    fonteId: r.fonte_id as string,
    fonte: (r.fonte as string) ?? '',
    enviadoEm: r.enviado_em as string,
    recebidoEm: r.recebido_em as string,
    formulario: txt(r.formulario),
    paginaUrl: txt(r.pagina_url),
    paginaTitulo: txt(r.pagina_titulo),
    nome: txt(r.nome),
    email: txt(r.email),
    telefone: txt(r.telefone),
    mensagem: txt(r.mensagem),
    campos: (r.campos as Record<string, unknown>) ?? {},
    utmSource: txt(r.utm_source),
    utmMedium: txt(r.utm_medium),
    utmCampaign: txt(r.utm_campaign),
    utmTerm: txt(r.utm_term),
    utmContent: txt(r.utm_content),
    gclid: txt(r.gclid),
    fbclid: txt(r.fbclid),
    referrer: txt(r.referrer),
    ip: txt(r.ip),
    userAgent: txt(r.user_agent),
  }
}

// O PostgREST corta em 1.000 linhas por resposta: busca em páginas até acabar.
const PAGINA = 1000
const LIMITE = 20000

/** Os leads do período (dia de São Paulo, fim incluído), do mais recente para o mais antigo. */
export async function listarLeads(intervalo: Intervalo, marcaId: string | null): Promise<{ leads: Lead[]; cortado: boolean }> {
  const leads: Lead[] = []
  for (let de = 0; de < LIMITE; de += PAGINA) {
    let q = supabase
      .from('vw_leads')
      .select('*')
      .gte('dia', intervalo.inicio)
      .lte('dia', intervalo.fim)
      .order('enviado_em', { ascending: false })
      .order('id')
      .range(de, de + PAGINA - 1)
    if (marcaId) q = q.eq('marca_id', marcaId)
    const { data, error } = await q
    if (error) throw new Error(error.message)
    leads.push(...(data ?? []).map(paraLead))
    if ((data ?? []).length < PAGINA) return { leads, cortado: false }
  }
  return { leads, cortado: true }
}

/** Quantos leads no intervalo — para comparar com o período anterior sem baixar as linhas. */
export async function contarLeads(intervalo: Intervalo, marcaId: string | null): Promise<number> {
  let q = supabase.from('vw_leads').select('id', { count: 'exact', head: true }).gte('dia', intervalo.inicio).lte('dia', intervalo.fim)
  if (marcaId) q = q.eq('marca_id', marcaId)
  const { count, error } = await q
  if (error) throw new Error(error.message)
  return count ?? 0
}

/** Pedido de exclusão do titular. O RLS barra em silêncio: confere que apagou. */
export async function apagarLead(id: string): Promise<void> {
  const { data, error } = await supabase.from('leads').delete().eq('id', id).select('id')
  if (error) throw new Error(error.message)
  if (!data?.length) throw new Error('Você não tem permissão para apagar este lead')
}

// ---------------------------------------------------------------------------
// Sites conectados
// ---------------------------------------------------------------------------

export type SiteLeads = {
  id: string
  marcaId: string
  nome: string
  chaveInicio: string
  ativa: boolean
  criadaEm: string
  ultimoLeadEm: string | null
}

export async function listarSites(): Promise<SiteLeads[]> {
  const { data, error } = await supabase
    .from('lead_fontes')
    .select('id, marca_id, nome, chave_inicio, ativa, criada_em, ultimo_lead_em')
    .order('criada_em')
  if (error) throw new Error(error.message)
  return (data ?? []).map((r) => ({
    id: r.id,
    marcaId: r.marca_id,
    nome: r.nome,
    chaveInicio: r.chave_inicio,
    ativa: r.ativa,
    criadaEm: r.criada_em,
    ultimoLeadEm: r.ultimo_lead_em,
  }))
}

const semPermissao = (msg: string) => (/permiss/i.test(msg) ? 'Você não tem permissão para conectar sites desta marca' : msg)

/** Conecta um site e devolve a chave — ela só aparece agora. */
export async function conectarSite(marcaId: string, nome: string): Promise<{ id: string; chave: string }> {
  const { data, error } = await supabase.rpc('conectar_site_leads', { p_marca_id: marcaId, p_nome: nome })
  if (error) throw new Error(semPermissao(error.message))
  return data as { id: string; chave: string }
}

export async function trocarChaveSite(id: string): Promise<{ id: string; chave: string }> {
  const { data, error } = await supabase.rpc('trocar_chave_site_leads', { p_fonte_id: id })
  if (error) throw new Error(semPermissao(error.message))
  return data as { id: string; chave: string }
}

export async function ativarSite(id: string, ativa: boolean): Promise<void> {
  const { data, error } = await supabase.from('lead_fontes').update({ ativa }).eq('id', id).select('id')
  if (error) throw new Error(error.message)
  if (!data?.length) throw new Error('Você não tem permissão para alterar este site')
}

/** O que o plugin do WordPress pede, além da chave do site. */
export const configuracaoDoPlugin = () => ({
  url: String(import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/+$/, ''),
  chavePublica: String(import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY || ''),
})
